/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getNextAbleResults } from 'src/core/utils/batchPromises';
import { getCustomTextTrainedModelService } from 'src/customText/core/services/trainedModels/customTextTrainedModelService';
import { getCustomTextTrainingService } from 'src/customText/core/services/training/customTextTrainingService';
import { trainingJobInfoSwaggerToTrainingJobInfoStore } from 'src/customText/modules/handlers/customTextTrainingHandlerGroup/customTextTrainingHandlerGroupMappers';
import { customTextTrainedModelsSwaggerToStores } from 'src/customText/modules/handlers/trainedModels/customTextTrainedModelMappers';
import { TrainingModelDto } from 'src/modules/modelTraining/components/SubmitTrainingJobPanel';
import { TrainingJobStore } from 'src/modules/modelTraining/stores/TrainingJobInfoStore';
import { NewTrainingJobSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';
import { getTrainingJobOperationHandler } from 'src/modules/modelTraining/utils/trainingUtils';

export type CustomTextTrainingHandlerGroup = ReturnType<typeof createCustomTextTrainingHandlerGroup>;

export const createCustomTextTrainingHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore, lifetime } = dependencies;

    const trainCustomTextModel = async ({ model, context }: HandlerParams<{ model: TrainingModelDto }>) => {
        const {
            name: projectName,
            trainingJobStoreHouse,
            trainedModels,
            modelsGuidanceMap,
            modelsEvaluationMap
        } = rootStore.currentCustomTextProjectStore;

        let jobId: string;
        const { modelName: trainingModelName } = model;
        const trainingService = getCustomTextTrainingService({ context });

        try {
            const statusUrl = await trainingService.initiateTraining({
                projectName,
                trainingModelName,
                evaluationOptions: {
                    kind: model.datasetSplitMode,
                    trainingSplitPercentage: model.trainingSplitPercentage,
                    testingSplitPercentage: model.testingSplitPercentage
                },
                trainingConfigVersion: model.trainingConfigVersion
            });
            jobId = new URL(statusUrl).pathname.split('/').pop();
            trainingJobStoreHouse.add(new TrainingJobStore(jobId, { status: 'inProgress', modelId: trainingModelName }));
            await trainingService.pollTraining({
                statusUrl,
                operationHandler: getTrainingJobOperationHandler(lifetime, trainingJobStoreHouse)
            });

            if (trainingJobStoreHouse.findById(jobId).status === 'succeeded') {
                if (modelsEvaluationMap.has(trainingModelName)) {
                    modelsEvaluationMap.delete(trainingModelName);
                }

                if (modelsGuidanceMap.has(trainingModelName)) {
                    modelsGuidanceMap.delete(trainingModelName);
                }

                const trainedModel = await getCustomTextTrainedModelService().getTrainedModel({ projectName, trainingModelName });

                if (trainedModels.items.find(i => i.name === trainingModelName)) {
                    trainedModels.replace(customTextTrainedModelsSwaggerToStores([trainedModel])[0]);
                } else {
                    trainedModels.batchAdd(customTextTrainedModelsSwaggerToStores([trainedModel]));
                }
            }
        } catch (e) {
            if (jobId) {
                trainingJobStoreHouse.findById(jobId).setStatus('failed');
            }

            throw e;
        }
    };

    const pollCustomTextTrainingJobs = async (params?: HandlerParams<object>) => {
        const { name: projectName, trainingJobStoreHouse } = rootStore.currentCustomTextProjectStore;

        const trainingService = getCustomTextTrainingService({ context: params?.context });
        trainingJobStoreHouse.tracker.setState('running');
        const trainingJobs = await getNextAbleResults<'value', NewTrainingJobSwagger>(
            (nextPageLink: string) => trainingService.getTrainingJobs({ nextPageLink, projectName }),
            'value'
        );

        if (trainingJobs.length === 0) {
            trainingJobStoreHouse.tracker.setState('completed');
            return;
        }

        trainingJobStoreHouse.setItems(trainingJobs.map(trainingJobInfoSwaggerToTrainingJobInfoStore));

        const trainingJobsInProgress = trainingJobs
            .filter(j => j.status === 'notStarted' || j.status === 'running')
            .sort((j1, j2) => new Date(j2.createdDateTime).getMilliseconds() - new Date(j1.createdDateTime).getMilliseconds());

        if (trainingJobsInProgress.length === 0) {
            trainingJobStoreHouse.tracker.setState('completed');
            return;
        }

        trainingJobsInProgress.forEach(
            async job =>
                await trainingService.pollTraining({
                    projectName,
                    jobId: job.jobId,
                    operationHandler: getTrainingJobOperationHandler(lifetime, trainingJobStoreHouse)
                })
        );
        trainingJobStoreHouse.tracker.setState('completed');
    };

    const cancelCustomTextTraining = async ({ jobId, context }: HandlerParams<{ jobId: string }>) => {
        const { name: projectName, trainingJobStoreHouse } = rootStore.currentCustomTextProjectStore;

        const trainingService = getCustomTextTrainingService({ context });

        await trainingService.cancelTraining({ projectName, jobId });

        const trainingJobs = await getNextAbleResults<'value', NewTrainingJobSwagger>(
            (nextPageLink: string) => trainingService.getTrainingJobs({ nextPageLink, projectName }),
            'value'
        );

        const trainingJobStatus = trainingJobs.find(job => job.jobId === jobId)?.status;

        if (trainingJobStatus) {
            trainingJobStoreHouse.findById(jobId).setStatus(trainingJobStatus);
        }
    };

    return { trainCustomTextModel, pollCustomTextTrainingJobs, cancelCustomTextTraining };
};
